import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import { Save, ArrowLeft, Loader2, ImagePlus } from "lucide-react";

export default function ContractedInstitutionManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    institutionNameTr: "",
    institutionNameEn: "",
    institutionNameAr: "",
    descriptionTr: "",
    descriptionEn: "",
    descriptionAr: "",
    institutionUrl: "",
    institutionLogo: null,
    preview: "",
    isActive: true,
    sortOrder: 1,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const settingsRes = await api.get("/setting");
        setSettings(settingsRes.data);

        if (id) {
          const res = await api.get(`/contractedinstitution/${id}`);
          const d = res.data;
          setFormData({
            institutionNameTr: d.institutionNameTr || "",
            institutionNameEn: d.institutionNameEn || "",
            institutionNameAr: d.institutionNameAr || "",
            descriptionTr: d.descriptionTr || "",
            descriptionEn: d.descriptionEn || "",
            descriptionAr: d.descriptionAr || "",
            institutionUrl: d.institutionUrl || "",
            institutionLogo: null,
            preview: d.institutionLogo
              ? `${process.env.REACT_APP_UPLOAD_URL}/${d.institutionLogo}`
              : "",
            isActive: d.isActive ?? true,
            sortOrder: d.sortOrder || 1,
          });
        }
      } catch {
        toast.error("Veriler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFormData({ ...formData, institutionLogo: f });
      const reader = new FileReader();
      reader.onload = (ev) =>
        setFormData((p) => ({ ...p, preview: ev.target.result }));
      reader.readAsDataURL(f);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.institutionNameTr.trim() && !formData.institutionNameEn.trim()) {
      return toast.error("En az bir dilde kurum adı gerekli");
    }
    setSaving(true);

    const data = new FormData();
    data.append("InstitutionNameTr", formData.institutionNameTr);
    data.append("InstitutionNameEn", formData.institutionNameEn);
    data.append("InstitutionNameAr", formData.institutionNameAr);
    data.append("DescriptionTr", formData.descriptionTr);
    data.append("DescriptionEn", formData.descriptionEn);
    data.append("DescriptionAr", formData.descriptionAr);
    data.append("InstitutionUrl", formData.institutionUrl);
    data.append("IsActive", formData.isActive);
    data.append("SortOrder", formData.sortOrder);
    if (formData.institutionLogo) data.append("file", formData.institutionLogo);

    try {
      if (id) {
        await api.put(`/contractedinstitution/${id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Kurum güncellendi");
      } else {
        await api.post("/contractedinstitution", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Kurum eklendi");
      }
      navigate("/contracted-institution-list");
    } catch {
      toast.error(id ? "Kurum güncellenemedi" : "Kurum eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );

  if (!settings && !id)
    return (
      <div className="page-loading">
        <p>Ayarlar bulunamadı.</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>{id ? "Kurum Düzenle" : "Kurum Ekle"}</h2>
          <p className="page-subtitle">
            {id ? "Mevcut kurumu düzenleyin" : "Yeni anlaşmalı kurum ekleyin"}
          </p>
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/contracted-institution-list")}
        >
          <ArrowLeft size={18} /> Listeye Dön
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-layout-2col">
          {/* Sol Kolon - İçerik */}
          <div className="form-main">
            {(!settings || settings.turkish) && (
              <div className="content-card">
                <h3 className="card-title">🇹🇷 Türkçe İçerik</h3>
                <div className="form-field">
                  <label>Kurum Adı</label>
                  <input
                    type="text"
                    name="institutionNameTr"
                    value={formData.institutionNameTr}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Türkçe kurum adı"
                  />
                </div>
                <div className="form-field">
                  <label>Açıklama</label>
                  <textarea
                    name="descriptionTr"
                    value={formData.descriptionTr}
                    onChange={handleChange}
                    className="form-input"
                    rows={4}
                    placeholder="Türkçe açıklama"
                  />
                </div>
              </div>
            )}

            {(!settings || settings.english) && (
              <div className="content-card">
                <h3 className="card-title">🇬🇧 English Content</h3>
                <div className="form-field">
                  <label>Institution Name</label>
                  <input
                    type="text"
                    name="institutionNameEn"
                    value={formData.institutionNameEn}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="English institution name"
                  />
                </div>
                <div className="form-field">
                  <label>Description</label>
                  <textarea
                    name="descriptionEn"
                    value={formData.descriptionEn}
                    onChange={handleChange}
                    className="form-input"
                    rows={4}
                    placeholder="English description"
                  />
                </div>
              </div>
            )}

            {(!settings || settings.arabic) && (
              <div className="content-card">
                <h3 className="card-title">🇸🇦 المحتوى العربي</h3>
                <div className="form-field">
                  <label>اسم المؤسسة</label>
                  <input
                    type="text"
                    name="institutionNameAr"
                    value={formData.institutionNameAr}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="اسم المؤسسة"
                    dir="rtl"
                  />
                </div>
                <div className="form-field">
                  <label>الوصف</label>
                  <textarea
                    name="descriptionAr"
                    value={formData.descriptionAr}
                    onChange={handleChange}
                    className="form-input"
                    rows={4}
                    placeholder="الوصف"
                    dir="rtl"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sağ Kolon - Ayarlar */}
          <div className="form-sidebar">
            <div className="content-card">
              <h3 className="card-title">Logo</h3>
              <div className="image-upload-area">
                {formData.preview ? (
                  <div className="image-preview logo-preview">
                    <img src={formData.preview} alt="Logo" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() =>
                        setFormData((p) => ({
                          ...p,
                          institutionLogo: null,
                          preview: "",
                        }))
                      }
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className="upload-placeholder">
                    <ImagePlus size={32} />
                    <span>Logo Yükle</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      hidden
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="content-card">
              <h3 className="card-title">Ayarlar</h3>
              <div className="form-field">
                <label>Website URL</label>
                <input
                  type="url"
                  name="institutionUrl"
                  value={formData.institutionUrl}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-field">
                <label>Sıralama</label>
                <input
                  type="number"
                  name="sortOrder"
                  value={formData.sortOrder}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                />
              </div>

              <div className="form-field">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                  <span>Aktif</span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="spin" /> Kaydediliyor...
                </>
              ) : (
                <>
                  <Save size={18} /> {id ? "Güncelle" : "Kaydet"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
